import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { UserService } from 'projects/lib-shared-core/src/public-api';

import {
  ActionPlanChecklistResponse,
  ActionPlanChecklistRequest,
  UpdateActionPlanChecklistItemRequest,
  ToggleChecklistItemCompleteRequest,
} from '../interfaces/svc-modal-action-plan.interface';

@Injectable({
  providedIn: 'root',
})
export class SvcActionPlanChecklistService {
  readonly #http = inject(HttpClient);
  readonly #appConfig = inject(AppEnvironmentConfig);
  readonly #userService = inject(UserService);

  readonly #api = this.#appConfig.APIs.apiUrlActionPlan;

  public getCurrentSiteId(): number | undefined {
    return this.#userService.user?.lastSiteId ?? this.#userService.user?.originSiteId;
  }

  public getChecklist(actionPlanId: number): Observable<ActionPlanChecklistResponse> {
    return this.#http.get<ActionPlanChecklistResponse>(
      `${this.#api}/ActionPlans/${actionPlanId}/Checklist`,
      { headers: this.#headers() }
    );
  }

  public createChecklistItem(actionPlanId: number, request: ActionPlanChecklistRequest): Observable<number> {
    return this.#http.post<number>(
      `${this.#api}/ActionPlans/${actionPlanId}/Checklist`,
      request,
      { headers: this.#headers() }
    );
  }

  public updateChecklistItem(actionPlanId: number, request: UpdateActionPlanChecklistItemRequest): Observable<void> {
    return this.#http.put<void>(
      `${this.#api}/ActionPlans/${actionPlanId}/Checklist/${request.taskUniqueId}`,
      request,
      { headers: this.#headers() }
    );
  }

  public updateChecklistItemStatus(actionPlanId: number, taskUniqueId: number, isCompleted: boolean, siteId?: number): Observable<void> {
    const request: ToggleChecklistItemCompleteRequest = {
      taskUniqueId,
      actionPlanUniqueId: actionPlanId,
      isCompleted,
      lastUpdateUserId: this.#userService.userId$,
      // Callers pass the action plan's siteId; the user's current site is only
      // a fallback and can be wrong for cross-site plans (BUG-69194).
      siteId: siteId ?? this.getCurrentSiteId() ?? 0,
    };
    return this.#http.patch<void>(
      `${this.#api}/ActionPlans/${actionPlanId}/Checklist/${taskUniqueId}/Complete`,
      request,
      { headers: this.#headers() }
    );
  }

  public deleteChecklistItem(actionPlanId: number, itemId: number): Observable<void> {
    return this.#http.delete<void>(
      `${this.#api}/ActionPlans/${actionPlanId}/Checklist/${itemId}`,
      { headers: this.#headers() }
    );
  }

  #headers(): { EnvironmentUrl: string } {
    const fromStorage = typeof localStorage !== 'undefined' ? localStorage.getItem('environmentUrl') : null;
    const fromWindow = typeof window !== 'undefined' ? window.location.origin : '';
    return { EnvironmentUrl: fromStorage || fromWindow };
  }
}
